import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-toastify';

import DocumentMetadataForm from 'components/Documents/DocumentMetadataForm';
import { documentsActionButtonSx } from 'components/Documents/documentsLayout';
import type { DocumentMetadataValues } from 'components/Documents/Scanner/types';
import { useCamera } from 'hooks/useCamera';
import { useDocumentScanner } from 'hooks/useDocumentScanner';
import { useScannerEngine } from 'hooks/useScannerEngine';
import { prepareImageDocumentUpload } from 'utils/pdfUtils';

import ScannerCameraView from './ScannerCameraView';
import ScannedPagesGrid from './ScannedPagesGrid';

interface DocumentScannerWorkflowProps {
  patientId: string;
  onUpload: (payload: {
    documentName: string;
    documentDescription?: string;
    generatedPdf: Blob;
    images: Blob[];
  }) => Promise<void>;
  onCancel: () => void;
}

type ScannerStep = 'camera' | 'review' | 'metadata';

const DocumentScannerWorkflow: React.FC<DocumentScannerWorkflowProps> = ({
  patientId,
  onUpload,
  onCancel,
}) => {
  const { scannerReady, loadingMessage, loadError, getScanner } = useScannerEngine();
  const { videoRef, isActive, cameraError, startCamera, stopCamera } = useCamera();
  const {
    pages,
    pageCount,
    maxPages,
    canCaptureMore,
    capturePage,
    removePage,
    clearPages,
  } = useDocumentScanner();

  const [step, setStep] = useState<ScannerStep>('camera');
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<DocumentMetadataValues>({
    documentName: '',
    documentDescription: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState<Blob | null>(null);
  const [scannedImages, setScannedImages] = useState<Blob[]>([]);
  const autoCameraStartedRef = useRef(false);

  const handleStartScan = useCallback(async () => {
    try {
      await startCamera();
      setStep('camera');
      setCaptureError(null);
    } catch {
      // cameraError state is set in hook
    }
  }, [startCamera]);

  useEffect(() => {
    if (
      scannerReady &&
      !autoCameraStartedRef.current &&
      step === 'camera' &&
      !loadError
    ) {
      autoCameraStartedRef.current = true;
      handleStartScan();
    }
  }, [scannerReady, step, loadError, handleStartScan]);

  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    try {
      setCaptureError(null);
      await capturePage(video, getScanner());
      setStep('review');
    } catch (error) {
      const message = (error as Error).message;
      if (message === 'PAGE_LIMIT_REACHED') {
        setCaptureError(`You can scan up to ${maxPages} pages only.`);
        toast(`Maximum of ${maxPages} pages reached.`, { type: 'warning' });
      } else {
        setCaptureError(message || 'Failed to capture page.');
      }
    }
  }, [capturePage, getScanner, maxPages, videoRef]);

  const handleCloseCamera = useCallback(() => {
    stopCamera();
    if (pageCount > 0) {
      setStep('review');
    } else {
      onCancel();
    }
  }, [onCancel, pageCount, stopCamera]);

  const handleFinishScanning = useCallback(async () => {
    if (pages.length === 0) {
      toast('Capture at least one page before finishing.', { type: 'info' });
      return;
    }

    setIsProcessing(true);
    stopCamera();

    try {
      const pageBlobs = pages.map((page) => page.imageBlob);
      const { generatedPdf: pdfBlob, images } = await prepareImageDocumentUpload(pageBlobs);

      setGeneratedPdf(pdfBlob);
      setScannedImages(images);
      setStep('metadata');
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast('Failed to generate PDF. Please try again.', { type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }, [pages, stopCamera]);

  const handleSubmit = useCallback(async () => {
    if (!metadata.documentName.trim()) {
      toast('Document name is required.', { type: 'error' });
      return;
    }

    if (!generatedPdf || scannedImages.length === 0) {
      toast('PDF is not ready. Please finish scanning again.', { type: 'error' });
      return;
    }

    if (!patientId) {
      toast('Patient information is missing.', { type: 'error' });
      return;
    }

    setIsProcessing(true);
    try {
      await onUpload({
        documentName: metadata.documentName.trim(),
        documentDescription: metadata.documentDescription.trim() ?? undefined,
        generatedPdf,
        images: scannedImages,
      });
      clearPages();
      onCancel();
    } catch (error) {
      console.error('Upload failed:', error);
      toast('Document upload failed. Please try again.', { type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }, [clearPages, generatedPdf, metadata, onCancel, onUpload, patientId, scannedImages]);

  const cameraErrorMessage =
    cameraError === 'denied'
      ? 'Camera permission was denied. Enable camera access in your browser settings.'
      : cameraError === 'unavailable'
        ? 'No camera was found on this device.'
        : cameraError === 'unknown'
          ? 'Unable to access the camera.'
          : null;

  if (step === 'metadata') {
    return (
      <Stack spacing={2}>
        <Typography variant='subtitle1' fontWeight={600}>
          Document details
        </Typography>
        <DocumentMetadataForm
          values={metadata}
          onChange={setMetadata}
          disabled={isProcessing}
        />
        <Button
          variant='contained'
          color='success'
          disabled={isProcessing ?? !metadata.documentName.trim()}
          onClick={handleSubmit}
          sx={documentsActionButtonSx}
        >
          {isProcessing ? <CircularProgress size={22} color='inherit' /> : 'Upload scanned document'}
        </Button>
        <Button variant='text' onClick={() => setStep('review')} disabled={isProcessing}>
          Back to review
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {loadError && <Alert severity='error'>{loadError}</Alert>}
      {!scannerReady && !loadError && (
        <Alert severity='info' icon={<CircularProgress size={18} />}>
          {loadingMessage}
        </Alert>
      )}
      {cameraErrorMessage && <Alert severity='error'>{cameraErrorMessage}</Alert>}
      {captureError && <Alert severity='warning'>{captureError}</Alert>}

      {!isActive && step === 'camera' && (
        <Button
          variant='contained'
          onClick={handleStartScan}
          disabled={!scannerReady || !!loadError}
          sx={documentsActionButtonSx}
        >
          Open camera
        </Button>
      )}

      {isActive && (
        <ScannerCameraView
          videoRef={videoRef}
          pageCount={pageCount}
          maxPages={maxPages}
          canCaptureMore={canCaptureMore}
          onCapture={handleCapture}
          onClose={handleCloseCamera}
        />
      )}

      <ScannedPagesGrid pages={pages} maxPages={maxPages} onRemove={removePage} />

      {pageCount > 0 && (
        <Stack spacing={1.5} alignItems='center'>
          {!isActive && step === 'review' && (
            <Button variant='outlined' onClick={handleStartScan} disabled={!canCaptureMore}>
              Capture next page
            </Button>
          )}
          <Button
            variant='contained'
            color='success'
            startIcon={isProcessing ? undefined : <CheckCircleOutlineIcon />}
            onClick={handleFinishScanning}
            disabled={isProcessing ?? pageCount === 0}
            sx={documentsActionButtonSx}
          >
            {isProcessing ? <CircularProgress size={22} color='inherit' /> : 'Finish scanning'}
          </Button>
        </Stack>
      )}

      <Button variant='text' onClick={onCancel} disabled={isProcessing}>
        Cancel
      </Button>
    </Stack>
  );
};

export default DocumentScannerWorkflow;
